/**
 * The finite list of campaign labels Vidi's own links carry (UTM_CONVENTION.md).
 *
 * Two readers share this one list, so they cannot drift apart:
 *  - App.tsx buckets a consented first touch into the campaign_visit event.
 *  - attribution.ts keeps these labels in the address bar, and withCampaignTags()
 *    carries them from /try onto /signup and /login. The labels are plain
 *    navigation context: reading them from the URL stores and sends nothing,
 *    so they work for visitors who are undecided or chose Essential Only.
 *
 * Anything not on this list (utm_term, a mistyped or invented value, ref, an
 * auth code) is not navigation context and is never carried. Adding a campaign
 * here is the only step needed to let its links keep their tags.
 */

export const CAMPAIGN_LABEL_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
] as const;
export type CampaignLabelKey = (typeof CAMPAIGN_LABEL_KEYS)[number];
type CampaignTags = Partial<Record<CampaignLabelKey, string>>;

export const APPROVED_CAMPAIGN_LABELS: Record<CampaignLabelKey, readonly string[]> = {
  utm_source: ['reddit', 'google', 'direct', 'referral'],
  utm_medium: ['post', 'comment', 'organic', 'referral'],
  utm_campaign: [
    'week01_soft_launch',
    'week02_german_learners',
    'week03_ukrainian_learners',
    'week01_photo_flow_pilot',
    'week02_learn_german',
    'week03_image_memory_science',
    'week03_engagement',
    'week04_spanish_learners',
    'week04_forgetting_curve',
    'week04_review',
  ],
  utm_content: [
    'alphaandbetausers',
    'betatests',
    'betatesters',
    'testmyapp',
    'germanlearning_resources',
    'languagelearning_resources',
  ],
};

/**
 * The approved labels present in a query, lowercased. One provided label that
 * is not approved voids the whole set (an edited link is not our campaign, so
 * a half-trusted carrier would misreport it), and the result is empty.
 */
export function approvedCampaignTags(params: URLSearchParams): CampaignTags {
  const tags: CampaignTags = {};
  for (const key of CAMPAIGN_LABEL_KEYS) {
    const raw = params.get(key);
    if (raw === null) continue;
    const value = raw.toLowerCase();
    if (!APPROVED_CAMPAIGN_LABELS[key].includes(value)) return {};
    tags[key] = value;
  }
  return tags;
}

/**
 * `path` plus the approved campaign labels of the current address, nothing else.
 * Used for in-app jumps between Try, signup and login. It is a real URL (not
 * router state) so a refresh or a copied link keeps the tags, and it copies no
 * other parameter: an auth code or a typed word must never ride along.
 */
export function withCampaignTags(path: string): string {
  const tags = approvedCampaignTags(new URLSearchParams(window.location.search));
  const carried = new URLSearchParams();
  for (const key of CAMPAIGN_LABEL_KEYS) {
    const value = tags[key];
    if (value !== undefined) carried.set(key, value);
  }
  const query = carried.toString();
  return query ? `${path}?${query}` : path;
}
